import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["sidebarInput", "dialogInput", "collapsedAnchor", "panel", "prompt", "frame", "result", "accountLink"]
  static values = { url: String, accountUrl: String }

  connect() {
    this.searchTimeout = null
    this.activeAnchor = null
    this.panelTarget.addEventListener("toggle", this.toggle)
    window.addEventListener("resize", this.position)
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    this.panelTarget.removeEventListener("toggle", this.toggle)
    window.removeEventListener("resize", this.position)
  }

  open() {
    this.activeAnchor = this.sidebarInputTarget
    this.activeInput = this.sidebarInputTarget
    this.showPanel()
    this.refreshQuery(this.sidebarInputTarget.value)
  }

  openFromCollapsed(event) {
    event?.preventDefault()
    this.activeAnchor = this.collapsedAnchorTarget
    this.activeInput = this.dialogInputTarget
    this.showPanel()
    this.refreshQuery(this.dialogInputTarget.value)
    requestAnimationFrame(() => this.dialogInputTarget.focus())
  }

  openFromShortcut() {
    if (this.sidebarCollapsed) {
      this.openFromCollapsed()
    } else {
      this.activeAnchor = this.sidebarInputTarget
      this.activeInput = this.sidebarInputTarget
      this.showPanel()
      this.refreshQuery(this.sidebarInputTarget.value)
      this.sidebarInputTarget.focus()
      this.sidebarInputTarget.select()
    }
  }

  search(event) {
    const query = event.currentTarget.value
    this.activeInput = event.currentTarget
    this.syncInputs(query, event.currentTarget)
    this.refreshQuery(query)
  }

  refreshQuery(query) {
    this.syncAccountLink(query)
    clearTimeout(this.searchTimeout)

    if (query.trim().length < 2) {
      this.promptTarget.hidden = false
      this.frameTarget.hidden = true
      this.frameTarget.removeAttribute("src")
      return
    }

    this.promptTarget.hidden = true
    this.frameTarget.hidden = false
    this.searchTimeout = setTimeout(() => this.load(query), 200)
  }

  submit(event) {
    event.preventDefault()
    const query = new FormData(event.currentTarget).get("q")?.trim()
    if (query?.length >= 2) Turbo.visit(this.searchUrl(query))
  }

  inputKeydown(event) {
    if (event.isComposing) return

    if (event.key === "ArrowDown" && this.resultTargets.length) {
      event.preventDefault()
      this.resultTargets[0].focus()
    } else if (event.key === "ArrowUp" && this.resultTargets.length) {
      event.preventDefault()
      this.resultTargets.at(-1).focus()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.panelTarget.hidePopover()
    }
  }

  resultKeydown(event) {
    const index = this.resultTargets.indexOf(event.currentTarget)

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.resultTargets[(index + 1) % this.resultTargets.length].focus()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (index === 0) this.activeInput?.focus()
      else this.resultTargets[index - 1].focus()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.panelTarget.hidePopover()
      this.activeInput?.focus()
    }
  }

  showPanel() {
    if (!this.panelTarget.matches(":popover-open")) this.panelTarget.showPopover()
    this.position()
  }

  load(query) {
    const source = this.searchUrl(query)

    if (this.frameTarget.src === source) this.frameTarget.reload()
    else this.frameTarget.src = source
  }

  searchUrl(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query.trim())
    return url.toString()
  }

  syncInputs(query, source) {
    const inputs = [this.sidebarInputTarget, this.dialogInputTarget]
    inputs.forEach((input) => {
      if (input !== source) input.value = query
    })
  }

  syncAccountLink(query) {
    if (!this.hasAccountLinkTarget) return

    const url = new URL(this.accountUrlValue, window.location.origin)
    if (query.trim()) url.searchParams.set("q", query.trim())
    this.accountLinkTarget.href = url.toString()
  }

  toggle = (event) => {
    const expanded = event.newState === "open"
    this.sidebarInputTarget.setAttribute("aria-expanded", expanded)
    this.dialogInputTarget.setAttribute("aria-expanded", expanded)
    if (expanded) this.position()
  }

  position = () => {
    if (!this.panelTarget.matches(":popover-open") || !this.activeAnchor) return

    requestAnimationFrame(() => {
      const anchor = this.activeAnchor.getBoundingClientRect()
      const panel = this.panelTarget.getBoundingClientRect()
      const margin = 8
      const preferredLeft = this.sidebarCollapsed ? anchor.right + margin : anchor.left
      const left = Math.min(Math.max(margin, preferredLeft), window.innerWidth - panel.width - margin)
      const top = Math.min(anchor.bottom + margin, window.innerHeight - panel.height - margin)

      this.panelTarget.style.left = `${left}px`
      this.panelTarget.style.top = `${Math.max(margin, top)}px`
    })
  }

  get sidebarCollapsed() {
    return this.element.closest("[data-controller~='sidebar']")?.hasAttribute("data-sidebar-collapsed")
  }
}
